import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, catchError, first, of, switchMap } from 'rxjs';
import { AuthBackendService, LoginStatus } from '../../../../services/auth-backend/auth-backend.service';
import { PunchoutService } from '../../../../services/punchout/punchout.service';
import { PunchoutCredential, PunchoutSetupCredentials } from '../../../../models/punchout';
import { ROLE_PUNCHOUT } from '../../../../models/user';
import { BackendFeatureService } from '../../../../services/backend-feature.service';

@Component({
  selector: 'lib-punchout-login',
  templateUrl: './punchout-login.component.html',
  styleUrls: ['./punchout-login.component.scss'],
  standalone: false
})
export class PunchoutLoginComponent implements OnInit {
  protected route = inject(ActivatedRoute);
  protected authBackendService = inject(AuthBackendService);
  protected punchoutService = inject(PunchoutService);
  protected backendFeatureService = inject(BackendFeatureService);

  protected failed = false;

  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    const username = params.get('username');
    const password = params.get('password');

    const setupCredentials: PunchoutSetupCredentials = {
      from: this.readCredential(params, 'fromDomain', 'fromIdentity'),
      to: this.readCredential(params, 'toDomain', 'toIdentity'),
      sender: this.readCredential(params, 'senderDomain', 'senderIdentity')
    };

    // The eprocurement system sends new parameters on each entry. This call replaces all five values.
    // Store them first. Then a failed login cannot keep the old hookUrl.
    this.punchoutService.setPunchoutParameters(
      params.get('hookUrl'),
      params.get('target'),
      params.get('protocol'),
      params.get('buyerCookie'),
      setupCredentials
    );

    this.establishSession(username, password)
      .pipe(first())
      .subscribe(status => {
        if (!status?.valid) {
          this.failed = true;
          return;
        }
        this.backendFeatureService
          .getAvailableFeatures(true)
          .pipe(first())
          .subscribe(() => this.redirectToHome());
      });
  }

  // A punchout user never logs out. On a second entry the old session cookie is still live.
  // Ask the backend who owns the cookie. Do not read the ngrx store: it holds the stale user.
  protected establishSession(username: string, password: string): Observable<LoginStatus> {
    return this.authBackendService.getUser().pipe(
      first(),
      catchError(() => of(null)),
      switchMap(user => {
        const current = user?.details;
        if (current?.username === username && current.roles?.includes(ROLE_PUNCHOUT)) {
          // The same punchout user is still logged in. Keep the session.
          return of({ valid: true });
        }
        // The session is stale, anonymous or for a different user. Remove it before the login.
        // Do not use AuthService.logout(): it dispatches Logout and deletes the parameters above.
        return this.authBackendService.logout().pipe(
          first(),
          catchError(() => of(undefined)),
          switchMap(() => this.authBackendService.loginUser(username, password))
        );
      })
    );
  }

  // Do a hard reload. The app then starts with the new session, and no client state is stale.
  protected redirectToHome(): void {
    window.location.href = '/';
  }

  protected readCredential(
    params: { get(name: string): string | null },
    domainKey: string,
    identityKey: string
  ): PunchoutCredential | undefined {
    const domain = params.get(domainKey);
    const identity = params.get(identityKey);
    if (!domain && !identity) {
      return undefined;
    }
    return { domain: domain ?? '', identity: identity ?? '' };
  }
}
